import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import ChatbotWidget from '../../components/ui/ChatbotWidget';
import TrackCard from './components/TrackCard';
import ComparisonTool from './components/ComparisonTool';
import CareerExplorer from './components/CareerExplorer';
import PersonalityQuiz from './components/PersonalityQuiz';
import FilterControls from './components/FilterControls';
import Icon from '../../components/AppIcon';

const LyceeOrientation = () => {
  const [expandedTrack, setExpandedTrack] = useState(null);
  const [comparisonTracks, setComparisonTracks] = useState([]);
  const [filters, setFilters] = useState({});
  const [filteredTracks, setFilteredTracks] = useState([]);

  // --- DONNÉES DU BAC MAROCAIN (Tags corrigés et complets) ---
  const baccalaureateTracks = [
    {
      id: 'sciences-math',
      name: 'Sciences Mathématiques (A & B)',
      shortDescription: 'L\'élite de l\'abstraction. Maths et Physique à très haut niveau.',
      icon: 'Atom',
      color: 'bg-blue-600',
      difficulty: 'Élevé',
      subjects: [
        'Mathématiques (Coeff 9)',
        'Physique-Chimie (Coeff 7)',
        'Sc. Ingénieur (SMB) / SVT (SMA)',
        'Philosophie',
        'Anglais'
      ],
      // AJOUT DE 'francais' et 'philosophie' dans les tags
      tags: ['mathematiques', 'physique', 'philosophie', 'anglais', 'sciences', 'francais'], 
      specializations: [
        'Sciences Math A (Théorique)',
        'Sciences Math B (Ingénierie)'
      ],
      careerProspects: [
        'Classes Préparatoires (MPSI)',
        'Génie Civil & Topographie',
        'Aéronautique',
        'Data Science'
      ],
      statistics: { successRate: '92%', employmentRate: '90%' }
    },
    {
      id: 'sciences-ex',
      name: 'Sciences Expérimentales',
      shortDescription: 'L\'équilibre entre théorie et observation du vivant.',
      icon: 'FlaskConical',
      color: 'bg-teal-600',
      difficulty: 'Moyen',
      subjects: [
        'SVT (Coeff 7 en SVT)',
        'Physique-Chimie (Coeff 7 en PC)',
        'Mathématiques (Coeff 7)',
        'Français',
        'Anglais'
      ],
      // AJOUT DE 'francais' et 'philosophie'
      tags: ['svt', 'physique', 'mathematiques', 'sciences', 'sante', 'francais', 'philosophie', 'anglais'],
      specializations: [
        'Sciences Physiques (PC)',
        'SVT',
        'Sciences Agronomiques'
      ],
      careerProspects: [
        'Médecine & Pharmacie',
        'Ingénierie (FST, ENSA)',
        'Biologie & Agriculture',
        'Infirmier / Paramédical'
      ],
      statistics: { successRate: '75%', employmentRate: '85%' }
    },
    {
      id: 'technique',
      name: 'Sciences et Technologies (STE & STM)',
      shortDescription: '100% Technique. Pas de SVT, pas d\'Histoire-Géo.',
      icon: 'Settings',
      color: 'bg-orange-600',
      difficulty: 'Moyen',
      subjects: [
        'Sciences de l\'Ingénieur (Coeff 8)',
        'Mathématiques (Coeff 6)',
        'Physique-Chimie (Coeff 6)',
        'Français',
        'Anglais'
      ],
      // Tags : 'francais' et 'philosophie' sont bien là (Français Coeff 4, Philo Coeff 2)
      tags: ['technologie', 'mathematiques', 'physique', 'ingenierie', 'electricite', 'mecanique', 'francais', 'philosophie', 'anglais'], 
      specializations: [
        'Génie Électrique (STE)',
        'Génie Mécanique (STM)'
      ],
      careerProspects: [
        'Ingénieur d\'état (ENSA, ENSAM)',
        'Technicien Spécialisé (EST, OFPPT)',
        'Industrie Automobile',
        'Électromécanique'
      ],
      statistics: { successRate: '82%', employmentRate: '88%' }
    },
    {
      id: 'economique',
      name: 'Sciences Économiques et Gestion',
      shortDescription: 'Le monde de l\'entreprise, la compta et l\'analyse de marché.',
      icon: 'TrendingUp',
      color: 'bg-green-600',
      difficulty: 'Moyen',
      subjects: [
        'Économie Générale (Coeff 6)',
        'Comptabilité (Coeff 4)',
        'Mathématiques (Coeff 4)',
        'Histoire-Géographie (Coeff 4)',
        'Philosophie'
      ],
      tags: ['economie', 'mathematiques', 'histoire', 'anglais', 'gestion', 'comptabilite', 'francais', 'philosophie'],
      specializations: [
        'Sciences Économiques (SE)',
        'Sciences de Gestion Comptable (SGC)'
      ],
      careerProspects: [
        'Expert-comptable',
        'ENCG & ISCAE',
        'Finance de marché',
        'Management & RH'
      ],
      statistics: { successRate: '80%', employmentRate: '83%' }
    },
    {
      id: 'litteraire',
      name: 'Lettres et Sciences Humaines',
      shortDescription: 'Littérature, langues, histoire et esprit critique.',
      icon: 'BookOpen',
      color: 'bg-purple-600',
      difficulty: 'Accessible',
      subjects: [
        'Arabe & Littérature (Coeff 4)',
        'Histoire-Géographie (Coeff 4)', 
        'Philosophie (Coeff 4)',
        'Anglais / Espagnol (Coeff 4)',
        'Français (Coeff 4)'
      ],
      tags: ['histoire', 'arabe', 'francais', 'anglais', 'philosophie', 'langues', 'droit', 'arts'],
      specializations: [
        'Lettres Modernes',
        'Sciences Humaines',
        'Langues Étrangères'
      ],
      careerProspects: [
        'Avocat & Juriste',
        'Journalisme & Communication',
        'Enseignement',
        'Psychologie',
        'Traduction'
      ],
      statistics: { successRate: '85%', employmentRate: '75%' }
    },
    {
        id: 'pro',
        name: 'Baccalauréat Professionnel',
        shortDescription: 'La voie directe vers un métier ou un BTS.',
        icon: 'Briefcase',
        color: 'bg-red-600',
        difficulty: 'Accessible',
        subjects: [
          'Pratique Professionnelle',
          'Mathématiques Appliquées',
          'Français',
          'Anglais'
        ],
        tags: ['technologie', 'francais', 'pratique', 'commerce', 'anglais'],
        specializations: [
          'Commerce & Vente',
          'Logistique',
          'Industrie Mécanique',
          'Froid et Climatisation'
        ],
        careerProspects: [
          'Insertion professionnelle immédiate',
          'Technicien Spécialisé',
          'Métiers de service',
          'Entrepreneuriat artisanal'
        ],
        statistics: { successRate: '70%', employmentRate: '80%' }
      }
  ];

  useEffect(() => {
    applyFilters();
  }, [filters]);

  const applyFilters = () => {
    let filtered = [...baccalaureateTracks];

    // 1. Filtrage par DOMAINE
    if (filters?.interestArea) {
      const interestMapping = {
        'sciences': ['sciences-math', 'sciences-ex'],
        'technologie': ['technique', 'pro', 'sciences-math'], 
        'economie': ['economique', 'pro'],
        'langues': ['litteraire'],
        'arts': ['litteraire', 'pro'],
        'sante': ['sciences-ex', 'sciences-math'],
        'droit': ['litteraire', 'economique'],
        'education': ['litteraire', 'sciences-math', 'sciences-ex']
      };
      
      const relevantTracks = interestMapping?.[filters?.interestArea] || [];
      filtered = filtered?.filter(track => relevantTracks?.includes(track?.id));
    }

    // 2. Filtrage par MATIÈRES (Correction bug Majuscules/Accents)
    if (filters?.preferredSubjects && filters?.preferredSubjects?.length > 0) {
      filtered = filtered.filter(track => {
        // La filière est gardée si elle contient AU MOINS UNE des matières cochées
        return filters.preferredSubjects.some(subjectFilter => 
            // On compare les tags en minuscules pour être sûr
            track.tags.includes(subjectFilter.toLowerCase())
        );
      });
    }

    // 3. Filtrage par DIFFICULTÉ
    if (filters?.difficultyLevels && filters?.difficultyLevels?.length > 0) {
      const difficultyMapping = {
        'accessible': 'Accessible',
        'moyen': 'Moyen',
        'eleve': 'Élevé'
      };
      
      filtered = filtered?.filter(track => 
        filters?.difficultyLevels?.some(level => 
          track?.difficulty === difficultyMapping?.[level]
        )
      );
    }

    // 4. Filtrage par OBJECTIF CARRIÈRE
    if (filters?.careerGoal) {
        const careerMapping = {
            'recherche': ['sciences-math', 'sciences-ex'],
            'entreprise': ['economique', 'technique', 'pro'],
            'fonction-publique': ['litteraire', 'economique'],
            'liberal': ['sciences-ex', 'litteraire', 'economique'],
            'enseignement': ['litteraire', 'sciences-math', 'sciences-ex'],
            'international': ['litteraire', 'economique', 'sciences-math'],
            'entrepreneuriat': ['economique', 'technique', 'pro'],
            'social': ['litteraire']
        };
        const allowed = careerMapping[filters.careerGoal] || [];
        filtered = filtered.filter(track => allowed.includes(track.id));
    }

    setFilteredTracks(filtered);
  };

  const handleTrackToggle = (trackId) => {
    setExpandedTrack(expandedTrack === trackId ? null : trackId);
  };

  const handleCompareTrack = (track) => {
    if (comparisonTracks?.find(t => t?.id === track?.id)) {
      setComparisonTracks(comparisonTracks?.filter(t => t?.id !== track?.id));
    } else if (comparisonTracks?.length < 3) {
      setComparisonTracks([...comparisonTracks, track]);
    }
  };

  const handleRemoveFromComparison = (trackId) => {
    setComparisonTracks(comparisonTracks?.filter(t => t?.id !== trackId));
  };

  const handleClearComparison = () => {
    setComparisonTracks([]);
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value
    }));
  };

  const handleResetFilters = () => {
    setFilters({});
  };

  const tracksToShow = filteredTracks?.length > 0 ? filteredTracks : baccalaureateTracks;

  return (
    <>
      <Helmet>
        <title>Orientation Lycée - Choisir sa filière baccalauréat | Afaqi</title>
        <meta 
          name="description" 
          content="Découvrez les différentes filières du baccalauréat marocain : Sciences Maths, PC, SVT, Éco, Technique... Comparez les programmes et débouchés." 
        />
      </Helmet>
      <div className="min-h-screen bg-background font-sans">
        <Header />
        
        <main className="container mx-auto px-4 py-8">
          <BreadcrumbNavigation />
          
          {/* Page Header */}
          <div className="text-center mb-12 animate-fade-in-up">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Icon name="GraduationCap" size={32} color="white" />
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4 font-heading">
              Orientation Lycée
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Sc. Maths, PC, SVT, Éco... ? <br/>Explorez les filières du bac marocain et trouvez celle qui fera décoller votre avenir.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Filter Results Info */}
              {filteredTracks?.length !== baccalaureateTracks?.length && (
                <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 animate-fade-in">
                  <div className="flex items-center space-x-2">
                    <Icon name="Filter" size={16} className="text-primary" />
                    <span className="text-primary font-medium">
                      {filteredTracks?.length} filière{filteredTracks?.length > 1 ? 's' : ''} correspond{filteredTracks?.length > 1 ? 'ent' : ''} à vos critères
                    </span>
                  </div>
                </div>
              )}

              {/* Track Cards */}
              <div className="space-y-6">
                {tracksToShow?.map((track) => (
                  <TrackCard
                    key={track?.id}
                    track={track}
                    isExpanded={expandedTrack === track?.id}
                    onToggle={() => handleTrackToggle(track?.id)}
                    onCompare={handleCompareTrack}
                    isInComparison={comparisonTracks?.some(t => t?.id === track?.id)}
                  />
                ))}
              </div>

              {/* No Results */}
              {filteredTracks?.length === 0 && (
                <div className="text-center py-12">
                  <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">Aucune filière trouvée</h3>
                  <p className="text-muted-foreground mb-4">Essayez de modifier vos filtres (matières ou domaine).</p>
                  <button
                    onClick={handleResetFilters}
                    className="text-primary hover:text-primary/80 font-medium underline"
                  >
                    Réinitialiser tout
                  </button>
                </div>
              )}

              {/* Comparison Tool */}
              <ComparisonTool
                comparisonTracks={comparisonTracks}
                onRemoveTrack={handleRemoveFromComparison}
                onClearAll={handleClearComparison}
              />
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Filter Controls */}
              <FilterControls
                filters={filters}
                onFilterChange={handleFilterChange}
                onResetFilters={handleResetFilters}
              />

              {/* Career Explorer */}
              <CareerExplorer />

              {/* Personality Quiz */}
              <PersonalityQuiz />

            </div>
          </div>
        </main>

        <ChatbotWidget />
      </div>
    </>
  );
};

export default LyceeOrientation;